import valueParser from 'postcss-value-parser';
import type { FunctionNode, Dimension, Node, DivNode, WordNode } from 'postcss-value-parser';
import { labToSRgb, lchToSRgb } from './color';

function onCSSFunction(node: FunctionNode) {
	const value = node.value;
	const rawNodes = node.nodes;
	const relevantNodes = rawNodes.slice().filter((x) => {
		return x.type !== 'comment' && x.type !== 'space';
	});

	let nodes: Lch | Lab | null = null;
	if (value === 'lab') {
		nodes = labFunctionContents(relevantNodes);
	} else if (value === 'lch') {
		nodes = lchFunctionContents(relevantNodes);
	}

	if (!nodes) {
		return;
	}

	if (relevantNodes.length > 3 && (!nodes.slash || !nodes.alpha)) {
		return;
	}

	// rename the Color function to `rgb`
	node.value = 'rgb';

	transformAlpha(node, nodes.slash, nodes.alpha);

	/** Extracted Color channels. */
	const [channelNode1, channelNode2, channelNode3] = channelNodes(nodes);
	const [channelDimension1, channelDimension2, channelDimension3] = channelDimensions(nodes);

	/** Corresponding Color transformer. */
	const toRGB = value === 'lab' ? labToSRgb : lchToSRgb;

	/** RGB channels from the source color. */
	const channelNumbers: [number, number, number] = [
		channelDimension1.number,
		channelDimension2.number,
		channelDimension3.number,
	].map(
		channelNumber => parseFloat(channelNumber),
	) as [number, number, number];

	const rgbValues = toRGB(
		channelNumbers,
	).map(
		channelValue => Math.max(Math.min(Math.round(channelValue * 2.55), 255), 0),
	);

	node.nodes.splice(node.nodes.indexOf(channelNode1) + 1, 0, commaNode());
	node.nodes.splice(node.nodes.indexOf(channelNode2) + 1, 0, commaNode());

	replaceWith(node.nodes, channelNode1, {
		...channelNode1,
		value: String(rgbValues[0]),
	});

	replaceWith(node.nodes, channelNode2, {
		...channelNode2,
		value: String(rgbValues[1]),
	});

	replaceWith(node.nodes, channelNode3, {
		...channelNode3,
		value: String(rgbValues[2]),
	});

}

export default onCSSFunction;

function commaNode(): DivNode {
	return {
		sourceIndex: 0,
		sourceEndIndex: 1,
		value: ',',
		type: 'div',
		before: '',
		after: '',
	};
}

function isNumericNode(node: Node): node is WordNode {
	if (!node || node.type !== 'word') {
		return false;
	}

	if (!canParseAsUnit(node)) {
		return false;
	}

	const unitAndValue = valueParser.unit(node.value);
	if (!unitAndValue) {
		return false;
	}

	return !!unitAndValue.number;
}

function isNumericNodeNumber(node): node is WordNode {
	if (!node || node.type !== 'word') {
		return false;
	}

	if (!canParseAsUnit(node)) {
		return false;
	}

	const unitAndValue = valueParser.unit(node.value);
	if (!unitAndValue) {
		return false;
	}

	return !!unitAndValue.number && unitAndValue.unit === '';
}

function isNumericNodeHueLike(node: Node): node is WordNode {
	if (!node || node.type !== 'word') {
		return false;
	}

	if (!canParseAsUnit(node)) {
		return false;
	}

	const unitAndValue = valueParser.unit(node.value);
	if (!unitAndValue) {
		return false;
	}

	return !!unitAndValue.number && (
		unitAndValue.unit === 'deg' ||
		unitAndValue.unit === 'grad' ||
		unitAndValue.unit === 'rad' ||
		unitAndValue.unit === 'turn' ||
		unitAndValue.unit === ''
	);
}

function isNumericNodePercentage(node: Node): node is WordNode {
	if (!node || node.type !== 'word') {
		return false;
	}

	if (!canParseAsUnit(node)) {
		return false;
	}

	const unitAndValue = valueParser.unit(node.value);
	if (!unitAndValue) {
		return false;
	}

	return unitAndValue.unit === '%';
}

function isNumericNodePercentageOrNumber(node: Node): node is WordNode {
	if (!node || node.type !== 'word') {
		return false;
	}

	if (!canParseAsUnit(node)) {
		return false;
	}

	const unitAndValue = valueParser.unit(node.value);
	if (!unitAndValue) {
		return false;
	}

	return unitAndValue.unit === '%' || unitAndValue.unit === '';
}

function isCalcNode(node: Node): node is FunctionNode {
	return node && node.type === 'function' && node.value === 'calc';
}

function isVarNode(node: Node): node is FunctionNode {
	return node && node.type === 'function' && node.value === 'var';
}

function isSlashNode(node: Node): node is DivNode {
	return node && node.type === 'div' && node.value === '/';
}

type Lch = {
	l: Dimension,
	lNode: Node,
	c: Dimension,
	cNode: Node,
	h: Dimension,
	hNode: Node,
	slash?: DivNode,
	alpha?: WordNode|FunctionNode,
}

function lchFunctionContents(nodes): Lch|null {
	if (!isNumericNodePercentage(nodes[0])) {
		return null;
	}

	if (!isNumericNodeNumber(nodes[1])) {
		return null;
	}

	if (!isNumericNodeHueLike(nodes[2])) {
		return null;
	}

	const out: Lch = {
		l: valueParser.unit(nodes[0].value) as Dimension,
		lNode: nodes[0],
		c: valueParser.unit(nodes[1].value) as Dimension,
		cNode: nodes[1],
		h: valueParser.unit(nodes[2].value) as Dimension,
		hNode: nodes[2],
	};

	normalizeHueNode(out.h);
	if (out.h.unit !== '') {
		return null;
	}

	if (isSlashNode(nodes[3])) {
		out.slash = nodes[3];
	}

	if ((isNumericNodePercentageOrNumber(nodes[4]) || isCalcNode(nodes[4]) || isVarNode(nodes[4]))) {
		out.alpha = nodes[4];
	}

	return out;
}

type Lab = {
	l: Dimension,
	lNode: Node,
	a: Dimension,
	aNode: Node,
	b: Dimension,
	bNode: Node,
	slash?: DivNode,
	alpha?: WordNode | FunctionNode,
}

function labFunctionContents(nodes): Lab|null {
	if (!isNumericNodePercentage(nodes[0])) {
		return null;
	}

	if (!isNumericNodeNumber(nodes[1])) {
		return null;
	}

	if (!isNumericNodeNumber(nodes[2])) {
		return null;
	}

	const out: Lab = {
		l: valueParser.unit(nodes[0].value) as Dimension,
		lNode: nodes[0],
		a: valueParser.unit(nodes[1].value) as Dimension,
		aNode: nodes[1],
		b: valueParser.unit(nodes[2].value) as Dimension,
		bNode: nodes[2],
	};

	if (isSlashNode(nodes[3])) {
		out.slash = nodes[3];
	}

	if ((isNumericNodePercentageOrNumber(nodes[4]) || isCalcNode(nodes[4]))) {
		out.alpha = nodes[4];
	}

	return out;
}

function isLab(x: Lch | Lab): x is Lab {
	if (typeof (x as Lab).a !== 'undefined') {
		return true;
	}

	return false;
}

function channelNodes(x: Lch | Lab): [Node, Node, Node] {
	if (isLab(x)) {
		return [x.lNode, x.aNode, x.bNode];
	}

	return [x.lNode, x.cNode, x.hNode];
}

function channelDimensions(x: Lch | Lab): [Dimension, Dimension, Dimension] {
	if (isLab(x)) {
		return [x.l, x.a, x.b];
	}

	return [x.l, x.c, x.h];
}

function transformAlpha(node: FunctionNode, slashNode: DivNode | undefined, alphaNode: WordNode | FunctionNode | undefined) {
	if (!slashNode || !alphaNode) {
		return;
	}

	node.value = 'rgba';
	slashNode.value = ',';
	slashNode.before = '';

	if (!isNumericNode(alphaNode)) {
		return;
	}

	const unitAndValue = valueParser.unit(alphaNode.value);
	if (!unitAndValue) {
		return;
	}

	if (unitAndValue.unit === '%') {
		// transform the Alpha channel from a Percentage to (0-1) Number
		unitAndValue.number = String(parseFloat(unitAndValue.number) / 100);
		alphaNode.value = String(unitAndValue.number);
	}
}

function replaceWith(nodes: Array<Node>, oldNode: Node, newNode: Node) {
	const index = nodes.indexOf(oldNode);
	nodes[index] = newNode;
}

function normalizeHueNode(dimension: Dimension) {
	switch (dimension.unit) {
		case 'deg':
			dimension.unit = '';
			return;
		case 'rad':
			// radians -> degrees
			dimension.unit = '';
			dimension.number = (parseFloat(dimension.number) * 180 / Math.PI).toString();
			return;

		case 'grad':
			// grades -> degrees
			dimension.unit = '';
			dimension.number = (parseFloat(dimension.number) * 0.9).toString();
			return;

		case 'turn':
			// turns -> degrees
			dimension.unit = '';
			dimension.number = (parseFloat(dimension.number) * 360).toString();
			return;
	}
}

function canParseAsUnit(node : Node): boolean {
	if (!node || !node.value) {
		return false;
	}

	try {
		return valueParser.unit(node.value) !== false;
	} catch (e) {
		return false;
	}
}
